import {createFileRoute} from '@tanstack/react-router';

import {parseNonEmpty, parseTrueFlag} from '@/lib/search-params';
import {AccountCodesPage} from '@/modules/admin-settings/pages/AccountCodesPage';

/**
 * URL state for the account-codes settings page. Both fields optional — the
 * settings nav links here with no params.
 *
 * Access is gated centrally by the existing `/admin/settings/**` rule in
 * `ROUTE_REQUIREMENTS` (`settings:view`), which is also what the API endpoint
 * behind this page requires — no finer rule needed here.
 */
export interface AccountCodesSearch {
  /**
   * Free-text search over code / description / account type, applied
   * client-side over the whole chart (the page loads all of it — see
   * `AccountCodesPage`). Kept in the URL so a search is shareable.
   *
   * No `page`: the page renders the chart as a tree, which cannot be paged
   * without cutting a leaf off its ancestors.
   */
  q?: string;
  /**
   * Show only the accounts something in Dyna posts to — a dozen or so out of
   * ~1,900. In the URL because it is the view worth linking someone to.
   */
  mapped?: true;
}

const validateAccountCodesSearch = (search: Record<string, unknown>): AccountCodesSearch => ({
  q: parseNonEmpty(search.q),
  ...(parseTrueFlag(search.mapped) ? {mapped: true as const} : {}),
});

export const Route = createFileRoute('/_authenticated/admin/settings/account-codes/')({
  validateSearch: validateAccountCodesSearch,
  component: AccountCodesPage,
});
